// src/utils/scoringEngine.js
// ============================================================
// TENAFI RISK SCORING ENGINE
// Weighted rules-based scoring (0-100) per tenant
// This is NOT machine learning — it's a deterministic formula
// that can be validated and adjusted based on pilot results.
// ============================================================

/**
 * Calculate risk score for a single tenant
 * @param {Object} tenant - Tenant record with payment history
 * @returns {Object} - { score, tier, color, label }
 */
export function calculateRiskScore(tenant) {
  // --- Factor 1: Days Late (35% weight) ---
  // How many days past due is the current balance?
  const daysLate = tenant.currentDaysLate || 0;
  // Normalize: 0 days = 0, 30+ days = 100
  const daysLateFactor = Math.min((daysLate / 30) * 100, 100);

  // --- Factor 2: Late Frequency (25% weight) ---
  // What percentage of months had late payments in the last 6-12 months?
  const history = tenant.paymentHistory || [];
  const lateMonths = history.filter(p => p.daysLate > 5).length;
  const totalMonths = Math.max(history.length, 1);
  const lateFrequencyFactor = (lateMonths / totalMonths) * 100;

  // --- Factor 3: Balance Ratio (20% weight) ---
  // How large is the outstanding balance relative to monthly rent?
  const balanceRatio = tenant.monthlyRent > 0
    ? tenant.currentBalance / tenant.monthlyRent
    : 0;
  // Normalize: 0x = 0, 3x+ = 100
  const balanceRatioFactor = Math.min((balanceRatio / 3) * 100, 100);

  // --- Factor 4: Drift Velocity (15% weight) ---
  // Is the tenant's payment timing getting worse over time?
  const driftVelocity = calculateDriftVelocity(history);
  // driftVelocity: negative = improving, 0 = stable, positive = worsening
  // Normalize: -5+ days improving = 0, stable = 30, +5+ days worsening = 100
  const driftFactor = Math.max(0, Math.min(((driftVelocity + 5) / 10) * 100, 100));

  // --- Factor 5: Partial Payment Pattern (5% weight) ---
  // How often does this tenant make partial payments?
  const partialPayments = history.filter(p => 
    p.amount > 0 && p.amount < tenant.monthlyRent * 0.95
  ).length;
  const partialFactor = Math.min((partialPayments / Math.max(totalMonths, 1)) * 200, 100);

  // --- Calculate weighted score ---
  const score = Math.round(
    (daysLateFactor * 0.35) +
    (lateFrequencyFactor * 0.25) +
    (balanceRatioFactor * 0.20) +
    (driftFactor * 0.15) +
    (partialFactor * 0.05)
  );

  // Clamp to 0-100
  const clampedScore = Math.max(0, Math.min(score, 100));

  // Determine risk tier
  const tier = getRiskTier(clampedScore);

  return {
    score: clampedScore,
    ...tier,
    factors: {
      daysLate: Math.round(daysLateFactor),
      lateFrequency: Math.round(lateFrequencyFactor),
      balanceRatio: Math.round(balanceRatioFactor),
      drift: Math.round(driftFactor),
      partialPayments: Math.round(partialFactor),
    },
  };
}


/**
 * Calculate drift velocity from payment history
 * Positive = getting worse, negative = improving, 0 = stable
 * Uses simple linear regression on days-late values
 * @param {Array} history - Payment history array
 * @returns {number} - Average change in days-late per month
 */
export function calculateDriftVelocity(history) {
  if (!history || history.length < 3) return 0;

  // Use the last 6 months (or whatever is available)
  const recent = history.slice(-6);
  const n = recent.length;
  
  // Simple linear regression: slope of days-late over time
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  recent.forEach((payment, i) => {
    const x = i;
    const y = payment.daysLate || 0;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const denominator = (n * sumX2) - (sumX * sumX);
  if (denominator === 0) return 0;

  // Slope = rate of change in days-late per month
  const slope = ((n * sumXY) - (sumX * sumY)) / denominator;
  return Math.round(slope * 10) / 10;
}


/**
 * Determine risk tier from score
 * @param {number} score - Risk score 0-100
 * @returns {Object} - { tier, color, bgColor, label }
 */
export function getRiskTier(score) {
  if (score >= 85) {
    return { tier: 'high', color: '#DC2626', bgColor: '#FEF2F2', label: 'High Risk' };
  } else if (score >= 60) {
    return { tier: 'medium', color: '#F59E0B', bgColor: '#FFFBEB', label: 'Medium Risk' };
  } else if (score >= 30) {
    return { tier: 'low', color: '#4B5563', bgColor: '#F3F4F6', label: 'Low Risk' };
  } else {
    return { tier: 'minimal', color: '#16A34A', bgColor: '#F0FDF4', label: 'Minimal Risk' };
  }
}


/**
 * Score all tenants in a property and sort by risk
 * @param {Array} tenants - Array of tenant records
 * @returns {Array} - Tenants with scores, sorted highest risk first
 */
export function scoreAndRankTenants(tenants) {
  return tenants
    .map(tenant => ({
      ...tenant,
      risk: calculateRiskScore(tenant),
    }))
    .sort((a, b) => b.risk.score - a.risk.score);
}
